import { Navigate, Outlet } from 'react-router-dom';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { useAdvisorFirm } from './useAdvisorFirm';
import { useWorkspaceKind, isKnownFamilyWorkspace } from '../workspace/useWorkspaceKind';

/**
 * Route half of steward-app#1742 (supersedes #1729): `/people/access` (Settings → Members until
 * steward-app#1921; `/settings/members` now redirects there) must go to the Firm page for
 * exactly the workspaces the People tab bar hides the Access TAB for, and nowhere else. The tab
 * half is `useAccessVisible` (`workspace/useAccessVisible.ts`, rendered by `PeopleLayout.tsx`),
 * which hides Access on the SAME `isKnownFamilyWorkspace` predicate
 * (`workspace/useWorkspaceKind.ts`) — both call it so a bookmarked or hand-typed
 * `/people/access` can never disagree with the tab bar about which page the roster lives on.
 * The component keeps its #1729 name; the route it wraps is what moved.
 *
 * ⚠️ **This used to key on `isFirmAdminHere`, and that was the bug #1742 fixes.** That
 * predicate needs the firm lookup to have SETTLED and matched this exact workspace before it
 * redirects, so a firm-pack workspace whose lookup had not resolved, had failed, or had
 * resolved to a different firm (an admin at two firms) all kept Members instead of redirecting
 * — the workspace already unambiguously answers "firm" from its pack in every one of those
 * cases, and `isKnownFamilyWorkspace` decides from that directly, with no wait on this lookup.
 * The destination is safe to send them to now that `RequireFirmAdmin` renders its own
 * loading/retry/"choose the right firm" state rather than a hard refusal for exactly those
 * cases (see that file), which is what made widening this redirect safe.
 *
 * Renders nothing while membership is still resolving (the same "decide nothing yet" rule
 * every gate in this file follows) — `isKnownFamilyWorkspace` itself returns `false` for a
 * `null` membership, so that alone would redirect a still-loading caller to the Firm page and
 * back; guarding here avoids that flash rather than relying on the predicate's fail-closed
 * default for it.
 *
 * ⚠️ **Same "decide nothing yet" rule extends to a degraded packs read whose firm lookup has
 * not settled either** (security review, steward-app#1742, 2026-09-23). `isKnownFamilyWorkspace`
 * answers `false` (hidden) for that combination, same as it does for firm and unknown — correct
 * for the Settings TAB, which just omits a tab. But this is a redirect, not an omission: sending
 * that caller to `/advisor/firm` while `RequireFirmAdmin` cannot yet tell whether they are a firm
 * admin either (its own `isFirmAdminWorkspace` needs the SAME lookup to have resolved and match)
 * strands them on that page's plain "this page is for your firm's admins" refusal — a dead end
 * for someone who may turn out to be an ordinary family-workspace member OR a firm admin once
 * the lookup settles, neither of which deserves a refusal in the meantime. Guarding here, rather
 * than only at the destination, avoids sending them anywhere until there is something to decide.
 */
export function RedirectFirmAdminToFirmPage() {
  const { activeWorkspaceId, activeMembership } = useWorkspace();
  const { advisorFor, isLoading, isError } = useAdvisorFirm();
  const workspaceKind = useWorkspaceKind();
  if (!activeMembership) return null;
  if (activeMembership.packsResolved === false && (isLoading || isError)) return null;
  const familyWorkspace = isKnownFamilyWorkspace(
    workspaceKind,
    activeMembership,
    activeWorkspaceId,
    advisorFor,
    isLoading || isError,
  );
  if (!familyWorkspace) {
    return <Navigate to="/advisor/firm/people" replace />;
  }
  return <Outlet />;
}
